import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';

import { UsersList } from './UsersList';
import { MAX_SELECTED_USERS_DISPLAYED } from '../hooks/useInfiniteUsersTargeting';
import type { NotifyUser } from '../types';

interface SelectedUsersProps {
  clearSelection: () => void;
  isMobile: boolean;
  maxTargetedUsers: number;
  removeUser: (user: NotifyUser) => void;
  selectedUsers?: NotifyUser[];
  selectedUsersCount: number;
}

const SelectedUsers = ({
  clearSelection,
  isMobile,
  maxTargetedUsers,
  removeUser,
  selectedUsers,
  selectedUsersCount,
}: SelectedUsersProps) => {
  const isMaxTargetedUsersReached = selectedUsersCount >= maxTargetedUsers;
  const hasMoreThanMaxTargetedUsers = selectedUsersCount > maxTargetedUsers;
  const hasMoreThanMaxDisplayedUsers =
    selectedUsersCount > MAX_SELECTED_USERS_DISPLAYED;

  return (
    <Box flexDirection="column">
      <Box justifyContent="space-between" alignItems="center" mb={3}>
        <Box alignItems="center">
          <Box font="h4-semibold" mr={3}>
            {s__('NotifyPeople|Selected (%{selectedUsers})').i__({
              selectedUsers: selectedUsersCount.toString() ?? '0',
            })}
          </Box>
          <Box
            font="metadata-regular-dove"
            color={isMaxTargetedUsersReached ? 'fire' : 'dove'}
          >
            {s__('NotifyPeople|%{maxTargetedUsers} max.').i__({
              maxTargetedUsers: maxTargetedUsers.toString(),
            })}
          </Box>
        </Box>
        {selectedUsersCount ? (
          <FakeLink onClick={clearSelection}>{__('Clear selection')}</FakeLink>
        ) : null}
      </Box>
      {isMaxTargetedUsersReached ? (
        <Box mb={-1} flexDirection="column">
          <AdditionalInformation
            type={hasMoreThanMaxTargetedUsers ? 'error' : 'info'}
            id="selected-users-error"
            mt={0}
          >
            {hasMoreThanMaxTargetedUsers
              ? s__(
                  'NotifyPeople|You have reached the maximum selection limit. Please deselect some users if you wish to add others.',
                )
              : s__(
                  'NotifyPeople|You have reached the maximum selection limit.',
                )}
          </AdditionalInformation>
        </Box>
      ) : null}
      {hasMoreThanMaxDisplayedUsers ? (
        <Box mb={-1}>
          <AdditionalInformation type="info" id="selected-users-info" mt={0}>
            {s__(
              "NotifyPeople|You're targeting %{count} people, but only the first %{max} are shown. All selected users will be notified.",
            ).i__({
              count: selectedUsersCount.toString(),
              max: MAX_SELECTED_USERS_DISPLAYED.toString(),
            })}
          </AdditionalInformation>
        </Box>
      ) : null}
      {selectedUsers?.length ? (
        <UsersList
          selectedUsers={selectedUsers}
          isMobile={isMobile}
          removeUser={removeUser}
        />
      ) : null}
    </Box>
  );
};

export default SelectedUsers;
